<template>
  <Container v-if="!pending && data" class="bg-surface-default-low px-16 pt-24">
    <RevForm
      :initial-values
      name="gift-cards-selection"
      :validate="validate()"
      @submit="handleSubmit"
    >
      <template #default="{ values, errors }">
        <div class="block gap-64 md:flex">
          <div
            class="sticky top-[168px] hidden max-w-[50%] grow self-start md:block"
          >
            <DesignPickers
              v-model="values.design"
              :design-pickers="data.designPickers"
            />
          </div>

          <div class="max-w-[480px]">
            <div class="mb-32 space-y-32">
              <div class="space-y-8">
                <h2 class="text-static-default-hi heading-1">
                  {{ i18n(translations.title) }}
                </h2>
                <div
                  ref="checkoutStickyBarVisibilityBeacon"
                  class="text-static-default-hi body-2"
                >
                  {{ i18n(translations.subtitle) }}
                </div>
              </div>

              <DesignPickers
                v-model="values.design"
                class="md:hidden"
                :design-pickers="data.designPickers"
              />

              <ReassuranceItems />

              <PricePickers
                v-model="values.price"
                :max-price="data.customAmount.maximum"
                :min-price="data.customAmount.minimum"
              />

              <GiftCardMessage
                v-model:message="values.message"
                v-model:recipient-name="values.recipientName"
                v-model:sender-name="values.senderName"
                :character-limit="data.message.characterLimit"
                :message-error="errors.message"
                :recipient-name-error="errors.recipientName"
                :sender-name-error="errors.senderName"
              />

              <SelectedDesign
                class="md:hidden"
                :design-pickers="data.designPickers"
                :model-value="values.design"
              />

              <div class="pt-32">
                <Proposal :price="values.price" />

                <RevButton
                  class="mt-12 w-full"
                  :loading
                  type="submit"
                  variant="primary"
                  >{{ i18n(translations.checkout) }}
                </RevButton>

                <PaymentIcons
                  class="my-24"
                  :methods="GIFT_CARDS_PAYMENT_METHOD"
                />

                <FormattedMessage
                  class="text-static-default-low caption"
                  :definition="translations.termsAndConditions"
                >
                  <template #termsAndConditionsLink>
                    <RevLink
                      target="_blank"
                      :to="
                        toNuxtLink({
                          type: 'internal',
                          name: CMS.LEGAL_PAGE,
                          params: {
                            pageName: LEGAL_PAGE_NAME.GIFT_CARDS,
                            locale: currentLocale,
                          },
                        })
                      "
                    >
                      {{ i18n(translations.termsAndConditionsLinkLabel) }}
                    </RevLink>
                  </template>
                </FormattedMessage>
              </div>

              <ReassuranceItems />

              <StickyBarBottom
                :loading
                :price="values.price"
                :show-bar="!isCheckoutStickyBarInViewport"
              />
            </div>
          </div>
        </div>
      </template>
    </RevForm>

    <FaqDrawer />
  </Container>
</template>

<script setup lang="ts">
import { navigateTo, useAsyncData } from '#imports'
import { ref, useTemplateRef } from 'vue'

import type { Currency } from '@backmarket/http-api/src/standards/Currency'
import PaymentIcons from '@backmarket/nuxt-layer-payment/methods/PaymentIcons'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, maxLength, required } from '@ds/components/Form'
import { RevLink } from '@ds/components/Link'
import { useElementVisibility } from '@vueuse/core'

import { CHECKOUT } from '~/scopes/checkout/routes-names'
import { CMS } from '~/scopes/cms/routes-names'
import { GIFT_CARDS_PAYMENT_METHOD } from '~/scopes/gift-cards/constant'
import { LEGAL_PAGE_NAME } from '~/scopes/legal/constants'
import Container from '~/scopes/product-core/components/presentationals/Container/Container.vue'

import translations from './GiftCards.translations'
import DesignPickers from './components/DesignPickers/DesignPickers.vue'
import FaqDrawer from './components/FaqDrawer/FaqDrawer.vue'
import GiftCardMessage from './components/GiftCardMessage/GiftCardMessage.vue'
import PricePickers from './components/PricePickers/PricePickers.vue'
import Proposal from './components/Proposal/Proposal.vue'
import ReassuranceItems from './components/ReassuranceItems/ReassuranceItems.vue'
import SelectedDesign from './components/SelectedDesign/SelectedDesign.vue'
import StickyBarBottom from './components/StickyBarBottom/StickyBarBottom.vue'

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const loading = ref(false)
const { openErrorToast } = useTheToast()

type giftCardForm =
  ApiSpecs['/api/gift-cards/v1/product-page/giftcard/add']['POST']['body']

const { data, pending } = await useAsyncData(() =>
  $httpFetch('/api/gift-cards/v1/product-page/giftcard', {
    method: 'GET',
  }),
)

const {
  market: { defaultCurrency },
} = useMarketplace()

const checkoutStickyBarVisibilityBeacon = useTemplateRef<HTMLDivElement>(
  'checkoutStickyBarVisibilityBeacon',
)
const isCheckoutStickyBarInViewport = useElementVisibility(
  checkoutStickyBarVisibilityBeacon,
)
const currentLocale = useI18nLocale()

const initialValues: giftCardForm = {
  design: data.value?.designPickers[0].id ?? '1',
  price: {
    amount: '50',
    currency: defaultCurrency as Currency,
  },
  recipientName: '',
  senderName: '',
  message: '',
}

function validate() {
  const characterLimit = data.value?.message.characterLimit ?? 250

  /* TODO: Price validation will be moved here in next PR */
  return makeValidate<giftCardForm>({
    recipientName: [required(i18n(translations.recipientNameRequired))],
    senderName: [required(i18n(translations.senderNameRequired))],
    message: [
      maxLength(
        characterLimit,
        i18n(translations.messageMaxLength, { characterLimit }),
      ),
    ],
  })
}

async function handleSubmit(values: giftCardForm) {
  loading.value = true
  try {
    const { requestId } = await $httpFetch(
      '/api/gift-cards/v1/product-page/giftcard/add',
      {
        method: 'POST',
        body: values,
      },
    )

    navigateTo({
      name: CHECKOUT.GIFT_CARDS.SELF,
      params: { requestId },
    })
  } catch (error) {
    openErrorToast()
  } finally {
    loading.value = false
  }
}
</script>
